import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en/translation.json";
import enMasterData from "./locales/en/masterData.json";
import enDoctors from "./locales/en/doctors.json";
import enSchedule from "./locales/en/schedule.json";
import enAppointments from "./locales/en/appointments.json";
import hi from "./locales/hi/translation.json";
import hiMasterData from "./locales/hi/masterData.json";
import hiDoctors from "./locales/hi/doctors.json";
import hiSchedule from "./locales/hi/schedule.json";
import hiAppointments from "./locales/hi/appointments.json";

const LANGUAGE_KEY = "cp-lang";

/** Languages offered in the switcher; names are written in their own script. */
export const LANGUAGES = [
  { code: "en", short: "EN", name: "English", locale: "en-IN" },
  { code: "hi", short: "हिं", name: "हिन्दी", locale: "hi-IN" },
] as const;
export type Language = (typeof LANGUAGES)[number]["code"];

const isLanguage = (value: string | null): value is Language => LANGUAGES.some((language) => language.code === value);

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return isLanguage(stored) ? stored : "en";
  } catch {
    return "en";
  }
}

/** BCP 47 locale for Intl formatting in the active language. */
export function activeLocale(): string {
  return LANGUAGES.find((language) => language.code === i18n.resolvedLanguage)?.locale ?? "en-IN";
}

// Resources are bundled (no extra network round-trip before first paint).
// Each feature owns its strings in locales/<lang>/<feature>.json, merged under
// its own key (t("appointments.…")). Add a language by adding a locales folder
// and an entry in LANGUAGES.
void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: { ...en, masterData: enMasterData, doctors: enDoctors, schedule: enSchedule, appointments: enAppointments } },
    hi: { translation: { ...hi, masterData: hiMasterData, doctors: hiDoctors, schedule: hiSchedule, appointments: hiAppointments } },
  },
  lng: initialLanguage(),
  supportedLngs: LANGUAGES.map((language) => language.code),
  fallbackLng: "en",
  interpolation: { escapeValue: false }, // React already escapes output
  returnNull: false,
});

document.documentElement.lang = i18n.resolvedLanguage ?? "en";

i18n.on("languageChanged", (language) => {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch {
    /* storage unavailable */
  }
  document.documentElement.lang = language;
});

export default i18n;
